// HouseholdPanel — pt89U (Feb 20, 2026, Randy).
//
// Extracted from CalendarSection.jsx during the Feb-20 cleanup pass so
// the parent file drops from ~1575 lines to ~1080 lines before Stage-C
// of the AI Re-Themer sweep begins. Zero behavior change from the
// pre-extraction version — the same function definition, same props,
// same JSX, same behavior. Only the file location and imports changed.
//
// Responsibility: the full household-setup panel that appears in the
// AI Calendar's Household sub-tab. Confirm Mode toggle, Family members
// row (add/remove/rename/promote-to-parent/PIN/impersonate), Kid
// profiles, Chores library, Reward store, Family digest, Family
// leaderboard, Family pings, Smart Create Chores, Quiet Hours.
//
// Contract:
//   • siloId, siloName — passed through to every sub-component +
//     endpoint call.
//   • settings — the whole calendar_settings object from
//     GET /calendar/{silo}/settings. Contains household.members[],
//     kid_profiles, is_owner, is_parent_role, quiet_hours, etc.
//   • onChanged() — parent should reload settings after any mutation.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Eye, EyeSlash, LockKey, Moon, PencilSimple, Trash, UsersThree, Sparkle, Plus, X, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useCollapsePref } from "@/lib/uiPrefs";
import InfoTip from "@/components/InfoTip";
import ChoresSection from "@/components/ChoresSection";
import KidProfilesPanel from "@/components/KidProfilesPanel";
import KidChoresModal from "@/components/KidChoresModal";
import KidScheduleModal from "@/components/KidScheduleModal";
import SmartCreateAllChores from "@/components/SmartCreateAllChores";
// pt89U (Feb 20, 2026, Randy) — Stage-C theme wrap. Each label a re-
// themed silo might want to rename ("Members" → "Team", "Kid profiles" →
// "Apprentices", "Chores library" → "Assignments library", etc.) reads
// through `useThemeLabel()` with a hardcoded default fallback.
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

// Feb 2026 — Option C from Randy: keep the two family sections but with
// clearer names, replace the inline 3-way segmented control with a status
// clearer names, replace the inline 3-way segmented control with a status
// PILL + pencil-icon edit flow, and add an eye tooltip explaining the three
// modes. Family-picker Kid profiles stay in KidProfilesPanel (their own eye tip
// explains why they're always "approval always").
const COLLAB_MODE_OPTS = [
  { v: "direct",   label: "Adds directly",  short: "Adds directly",  hint: "their items land straight on the calendar",
    pillCls: "bg-[#EAF3EC] text-[#2F6B3A] border border-[#B9D4BE]" },
  { v: "approval", label: "Needs approval", short: "Needs approval", hint: "their items wait in your inbox first — kid-safe",
    pillCls: "bg-white text-[#2F6B3A] border border-[#4A5D4E]" },
  { v: "off",      label: "Can't add",      short: "Can't add",      hint: "view only",
    pillCls: "bg-[#FBEBE4] text-[#A55A48] border border-[#EACAB8]" },
];

function HouseholdPanel({ siloId, siloName, settings, onChanged }) {
  // pt89U (Feb 20, 2026, Randy) — Stage-C theme label lookups. Read once
  // per render so the same string doesn't resolve twice. Every visible
  // label a re-themed silo might rename lives here.
  const tMembers        = useThemeLabel(T.THEME_KEY_HOUSEHOLD_MEMBERS);
  const tAddMember      = useThemeLabel(T.THEME_KEY_HOUSEHOLD_ADD_MEMBER);
  const tParent         = useThemeLabel(T.THEME_KEY_HOUSEHOLD_PARENT);
  const tKid            = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID);
  const tKidProfiles    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_KID_PROFILES);
  const tChoresLibrary  = useThemeLabel(T.THEME_KEY_HOUSEHOLD_CHORES_LIBRARY);
  const tRewardStore    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_REWARD_STORE);
  const tLeaderboard    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_LEADERBOARD);
  const tDigest         = useThemeLabel(T.THEME_KEY_HOUSEHOLD_DIGEST);
  const tQuietHours     = useThemeLabel(T.THEME_KEY_HOUSEHOLD_QUIET_HOURS);
  const tConfirmMode    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_CONFIRM_MODE);
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep additions.
  const tChoresBtn      = useThemeLabel(T.THEME_KEY_HOUSEHOLD_CHORES_BUTTON);
  const tScheduleBtn    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_SCHEDULE_BUTTON);
  const tInviteesHeader = useThemeLabel(T.THEME_KEY_HOUSEHOLD_INVITEES_HEADER);
  const tAddMembersHead = useThemeLabel(T.THEME_KEY_HOUSEHOLD_ADD_MEMBERS_HEADING);
  const tQuietRow       = useThemeLabel(T.THEME_KEY_HOUSEHOLD_QUIET_HOURS_ROW);
  // Reserved for future use / documentation:
  //   THEME_KEY_HOUSEHOLD_TITLE, THEME_KEY_HOUSEHOLD_OWNER
  const isOwner = !!settings?.is_owner;
  // Feb 19, 2026 (Randy Photo #3) — parent-role members get the FULL
  // Household Setup panel (Confirm Mode, Chores library, Add members,
  // Add kid, Quiet hours, Smart Create). Impersonation stays owner-only
  // as a privacy safeguard.
  const isParentRole = !!settings?.is_parent_role;
  const canEditHousehold = isOwner || isParentRole;
  const members = settings?.household?.members || [];
  // Feb 2026 (Randy pt89c) — owner is now included in the family row as the
  // top entry (was previously filtered out). Kids stay separate.
  const familyRows = members.filter((m) => !m.is_kid);
  const kids = members.filter((m) => m.is_kid);
  const [qs, setQs] = useState(settings?.quiet_hours?.quiet_start || "");
  const [qe, setQe] = useState(settings?.quiet_hours?.quiet_end || "");
  const [savingQuiet, setSavingQuiet] = useState(false);
  // Jun 2026 (Randy) — collapse memory for the "Add members" section (roster
  // + kid profiles + quiet hours). Server-backed via useCollapsePref; defaults
  // COLLAPSED so the section is tucked away until the owner opens it.
  const [addMembersCollapsed, toggleAddMembers] = useCollapsePref(siloId, "add_members", true);
  // Feb 2026 (Randy) — Confirm Mode toggle. Household-level. ON by default.
  const [confirmMode, setConfirmMode] = useState(true);
  useEffect(() => {
    api.get(`/calendar/${siloId}/confirm-mode`).then((r) => {
      setConfirmMode(!!r.data?.confirm_mode);
    }).catch(() => {});
  }, [siloId]);
  const flipConfirmMode = async () => {
    const next = !confirmMode;
    try {
      await api.post(`/calendar/${siloId}/confirm-mode`, { enabled: next });
      setConfirmMode(next);
      toast.success(next
        ? "Confirm Mode ON — kids' completions wait for parent approval."
        : "Confirm Mode OFF — trust system. Fewer pings.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't change."); }
  };
  // PIN management state — { user_id, pin, busy }.
  const [pinRow, setPinRow] = useState(null);
  // Feb 26, 2026 (Randy pt-member-exempt) — owner toggles a member into
  // "hidden mode" without deleting their settings. Row visual dims,
  // operational surfaces (leaderboard, chore assignment, Smart Create
  // Workout Schedule, reminders) filter them out. Untoggle restores
  // everything intact.
  const toggleExempt = async (m) => {
    if (!isOwner) return;
    const target = m.user_id;
    const nextExempt = !m.exempt;
    try {
      await api.post(`/calendar/${siloId}/members/${encodeURIComponent(target)}/exempt`, { exempt: nextExempt });
      toast.success(nextExempt
        ? `${m.name || "Member"} is now hidden from scheduling.`
        : `${m.name || "Member"} is back in the mix.`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update.");
    }
  };
  // has_pin lookup for owner + parent-pill members.
  const [pinMap, setPinMap] = useState({});
  const loadPins = async () => {
    if (!isOwner) return;
    try {
      const r = await api.get(`/calendar/${siloId}/household-pins`);
      setPinMap(r.data?.has_pin || {});
    } catch { /* non-fatal */ }
  };
  useEffect(() => { loadPins(); /* eslint-disable-next-line */ }, [siloId, isOwner, settings]);
  const savePin = async (userId, pinVal) => {
    try {
      await api.post(`/calendar/${siloId}/parent-pin`, { user_id: userId, pin: pinVal });
      toast.success(pinVal ? "PIN saved." : "PIN cleared.");
      setPinRow(null);
      loadPins();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save PIN."); }
  };
  // Owner→member impersonation (email members only). Kids' impersonation
  // just navigates to the family picker (they don't have their own JWT
  // pipeline; the picker is the correct "step into their shoes" surface).
  const impersonate = async (m) => {
    if (m.is_kid) {
      // Navigate to the family picker so the owner can tap the kid tile.
      window.location.href = `/family/${siloId}`;
      return;
    }
    if (m.is_owner) return; // self-impersonation is meaningless
    try {
      const r = await api.post(`/calendar/${siloId}/impersonate-member/${encodeURIComponent(m.user_id)}`);
      const t = r.data?.token;
      const u = r.data?.user;
      if (!t || !u) throw new Error("no token");
      try { localStorage.setItem("admin_original_token", sessionStorage.getItem("mysilo_token") || ""); } catch { /* */ }
      sessionStorage.setItem("mysilo_token", t);
      sessionStorage.setItem("mysilo_user", JSON.stringify(u));
      toast.success(`Now viewing as ${u.name || u.email}.`);
      setTimeout(() => { window.location.href = "/fan-portal"; }, 300);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't impersonate."); }
  };
  // Feb 2026 — inline edit flow (Option C). Clicking a family invitee's
  // pencil expands their row into a mode picker; Save persists via the
  // existing collab-mode endpoint, Cancel discards.
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  // Feb 2026 (Randy pt89c) — rename state (per-silo display-name override).
  // Applies to owner + email collaborators. Kids have their own rename in
  // KidProfilesPanel.
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Feb 2026 (Randy) — per-target Chores / Schedule modals. `target` is
  // the household member row (any collaborator OR kid).
  const [choresTarget, setChoresTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  // Feb 2026 (Randy pt89c) — inline invite widget (mini version of the
  // Settings → Users invite flow, reusing /users/search + /silos/{id}/invites).
  const [inviteQuery, setInviteQuery] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  useEffect(() => {
    if (!inviteOpen || !inviteQuery.trim() || inviteQuery.trim().length < 2) { setInviteResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search`, { params: { q: inviteQuery.trim() } });
        setInviteResults(r.data?.users || r.data?.results || []);
      } catch { setInviteResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [inviteQuery, inviteOpen]);
  const sendInvite = async (u) => {
    // Feb 2026 (Randy pt-invite-clicktime-precheck) — one-tap confirm so a
    // mis-clicked search result can be caught before it fires. Zero latency:
    // just the confirm dialog, no extra API call.
    const label = u.name ? `${u.name} <${u.email}>` : u.email;
    if (!window.confirm(`Invite ${label} to this silo?\n\nThey'll be added as a parent-role collaborator and will see this silo on their dashboard alongside their own.`)) return;
    setInviteBusy(true);
    try {
      await api.post(`/silos/${siloId}/invites`, { invitee_id: u.id });
      toast.success(`Invite sent to ${u.name || u.email}.`);
      setInviteQuery(""); setInviteResults([]); setInviteOpen(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't invite."); }
    finally { setInviteBusy(false); }
  };
  const removeCollab = async (m) => {
    if (!window.confirm(`Remove ${m.name} from this silo? They'll lose access to the calendar, capture pad and any household data here.`)) return;
    try {
      await api.delete(`/silos/${siloId}/collaborators/${m.user_id}`);
      toast.success(`${m.name} removed.`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove."); }
  };
  const startRename = (m) => { setRenamingId(m.user_id); setRenameDraft(m.name || ""); };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };
  const saveRename = async (m) => {
    const clean = (renameDraft || "").trim();
    try {
      await api.post(`/calendar/${siloId}/collab-rename`, { user_id: m.user_id, display_name: clean });
      toast.success(clean ? `Renamed to "${clean}" (calendar only).` : "Rename cleared — showing account name again.");
      cancelRename();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename."); }
  };

  // Feb 2026 (Randy) — promote-to-parent flow. Owner-only toggle; the
  // backend gates parent_ids updates so we can just fire the call and
  // reload on success.
  const toggleParent = async (m) => {
    try {
      await api.post(`/calendar/${siloId}/parent-flag`, { user_id: m.user_id, is_parent: !m.is_parent });
      toast.success(m.is_parent
        ? `${m.name} is no longer a Parent role.`
        : `${m.name} is now a Parent — they'll see the Family digest.`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update."); }
  };

  const startEditMember = (m) => {
    setEditingMemberId(m.user_id);
    setPendingMode(m.mode || "approval");
  };
  const cancelEditMember = () => { setEditingMemberId(null); setPendingMode(null); };
  const savePendingMode = async (m) => {
    if (!pendingMode) { cancelEditMember(); return; }
    try {
      await api.post(`/calendar/${siloId}/collab-mode`, { user_id: m.user_id, mode: pendingMode });
      toast.success(`${m.name}: ${COLLAB_MODE_OPTS.find((o) => o.v === pendingMode)?.label || pendingMode}.`);
      cancelEditMember();
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };
  const saveQuiet = async (clear = false) => {
    setSavingQuiet(true);
    try {
      await api.post(`/calendar/${siloId}/prefs`, clear ? { quiet_start: null, quiet_end: null } : { quiet_start: qs || null, quiet_end: qe || null });
      if (clear) { setQs(""); setQe(""); }
      toast.success(clear ? "Quiet hours cleared." : "Quiet hours saved — no pings in that window.");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setSavingQuiet(false); }
  };

  // For KidScheduleModal: pass the other household members as copy-from
  // candidates so parents can clone an existing person's grid. Note the
  // backend endpoints (`/kid-chores/{target_id}` and `/kid-availability/
  // {target_id}`) route on the `kid:` prefix, so we pass the full
  // `user_id` string verbatim.
  const copyCandidatesFor = (m) => {
    return members
      .filter((x) => !x.is_owner && x.user_id !== m?.user_id)
      .map((x) => ({
        id: x.user_id,
        name: `${x.emoji || (x.is_kid ? "🙂" : "👤")} ${x.name || "Member"}${x.is_kid ? " (kid)" : ""}`,
        availability: x.availability,
        notes: x.availability_notes,
      }));
  };

  return (
    <div className="mt-3 border hairline rounded-md p-3 bg-[#FBFAF6] space-y-4" data-testid="calendar-household-panel">
      {/* Confirm Mode — Feb 2026 (Randy). Owner-only. Household-wide.
          ON = kids' chore checks land as red-pending until a parent
          approves via the Master Dashboard (PIN) or from the in-app inbox.
          OFF = trust system. Fewer pings. */}
      {canEditHousehold && (
        <div className="flex items-center gap-3 flex-wrap px-3 py-2.5 rounded-md bg-white border hairline" data-testid="household-confirm-mode-row">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
            {tConfirmMode}
            <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={340} mobileCenter
              testid="confirm-mode-eye" label="What is Confirm Mode?">
              When <strong>ON</strong>, every kid&apos;s chore check-off lands as a <strong>red-pending</strong> item until a parent approves it — via the in-app inbox OR by tapping the red check on the Master Dashboard and entering their PIN. Points, streaks and badges only accrue after approval.{"\n\n"}
              When <strong>OFF</strong>, it&apos;s a trust system: a kid taps done → done. Fewer pings, less friction.
            </InfoTip>
          </div>
          <div className="flex-1" />
          <button onClick={flipConfirmMode} data-testid="household-confirm-mode-toggle"
            className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border-2 ${
              confirmMode
                ? "bg-[#2C6E92] text-white border-[#2C6E92]"
                : "bg-white text-[#6B6A68] border-black/10 hover:border-[#2C6E92] hover:text-[#2C6E92]"
            }`}>
            {confirmMode ? "ON" : "OFF"}
          </button>
        </div>
      )}
      {/* Chores library — sits at the top per Randy's spec, above Family
          invitees. Formatted like the Reward store row: a horizontal
          strip of label · property▼ · pets▼ · Browse suggestions · New chore. */}
      {canEditHousehold && <ChoresSection siloId={siloId} settings={settings} onChanged={onChanged} />}

      {/* Jun 2026 (Randy) — collapsible "Add members" section: tucks away the
          member roster, kid profiles, and quiet hours. Persists per user
          server-side via useCollapsePref (key add_members). */}
      <button type="button" onClick={toggleAddMembers} data-testid="add-members-chevron"
        aria-expanded={!addMembersCollapsed}
        title={addMembersCollapsed ? "Show members & quiet hours" : "Hide members & quiet hours"}
        className="w-full flex items-center gap-1.5 text-left mb-1 hover:opacity-80 transition-opacity">
        {addMembersCollapsed
          ? <CaretRight size={14} weight="bold" className="text-[#6B6A68]" />
          : <CaretDown size={14} weight="bold" className="text-[#6B6A68]" />}
        <UsersThree size={14} weight="duotone" className="text-[#4A5D4E]" />
        <span className="font-serif-display text-base text-[#1C1B1A]" data-testid="add-members-heading">{tAddMembersHead}</span>
      </button>
      {!addMembersCollapsed && (<>
      {canEditHousehold && (
        <div>
          <div className="mb-1.5 flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
              <UsersThree size={12} weight="duotone" /> {tInviteesHeader}
              <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={360} mobileCenter
                testid="family-invitees-eye" label="What are the three add-modes + the Parent tag?">
                <strong>Family invitees</strong> are real accounts (they have their own email login). The <strong>Owner</strong> sits at the top of the list; email invitees follow. Different from the <strong>Kid profiles</strong> below, which sign in on a shared device with a tile + optional PIN — no email, no password.{"\n\n"}
                Each row has three add-modes — tap the pencil to change (Owner is always direct):{"\n\n"}
                <strong>Adds directly</strong> — their calendar items land straight on the family calendar, no gate.{"\n\n"}
                <strong>Needs approval</strong> — the kid-safe default. Anything they add waits in your <em>approval inbox</em> first.{"\n\n"}
                <strong>Can&apos;t add</strong> — view only.{"\n\n"}
                <strong>+ Parent</strong> — promote a family member to Parent role: they stay free, see the Family digest, become a first-class recipient for household pings.{"\n\n"}
                Right of the header is a <strong>mini invite search</strong> — same behavior as Settings → Users. Removing here mirrors the settings surface (and vice-versa).
              </InfoTip>
            </div>
            <span className="flex-1" />
            {/* Mini invite widget — Feb 2026 (Randy pt89c). */}
            <div className="relative">
              <div className="flex items-center gap-1.5">
                <input value={inviteQuery}
                  onChange={(e) => { setInviteQuery(e.target.value); setInviteOpen(true); }}
                  onFocus={() => setInviteOpen(true)}
                  placeholder="Invite by name or email…"
                  data-testid="invite-search-input"
                  className="text-xs px-2.5 py-1.5 border hairline rounded bg-white w-[180px] sm:w-[220px] focus:outline-none focus:border-[#4A5D4E]" />
              </div>
              {inviteOpen && inviteQuery.trim().length >= 2 && (
                <div className="absolute top-full right-0 mt-1 w-[280px] max-h-[240px] overflow-y-auto bg-white border hairline rounded-md shadow-lg z-20"
                  data-testid="invite-search-results">
                  {inviteResults.length === 0 ? (
                    <div className="p-3 text-[11px] text-[#6B6A68] italic">
                      No matches. Ask them to sign up first, then search again.
                    </div>
                  ) : (
                    inviteResults.map((u) => (
                      <button key={u.id} onClick={() => sendInvite(u)} disabled={inviteBusy}
                        data-testid={`invite-search-result-${u.id}`}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-[#F0F1EE] text-sm">
                        <span className="flex-1 min-w-0">
                          <span className="block truncate text-[#1C1B1A]">{u.name || u.email}</span>
                          {u.email && u.name && <span className="block truncate text-[10px] text-[#6B6A68]">{u.email}</span>}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#4A5D4E] text-white">Invite</span>
                      </button>
                    ))
                  )}
                </div>
              )}
              {inviteOpen && <div className="fixed inset-0 z-10" onClick={() => setInviteOpen(false)} />}
            </div>
          </div>
          {familyRows.length === 0 ? (
            <div className="text-[11px] text-[#6B6A68] italic">
              No members yet — but that shouldn&apos;t happen, since you (the owner) always appear here.
            </div>
          ) : (
            <div className="space-y-1.5">
              {familyRows.map((m) => {
                const isTheOwner = !!m.is_owner;
                const currentOpt = isTheOwner
                  ? { short: "Owner", pillCls: "bg-[#1C1B1A] text-[#FDFCF8] border border-[#1C1B1A]", hint: "You own this silo — always adds directly." }
                  : (COLLAB_MODE_OPTS.find((o) => o.v === m.mode) || COLLAB_MODE_OPTS[1]);
                const isEditingMode = editingMemberId === m.user_id;
                const isRenaming = renamingId === m.user_id;
                if (isRenaming) {
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 flex-wrap bg-[#F8F1E2] border border-[#EAD9AE] rounded px-2.5 py-2" data-testid={`household-rename-row-${m.user_id}`}>
                      <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A6A24]">Rename (calendar only)</span>
                      <input value={renameDraft} onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(m); if (e.key === "Escape") cancelRename(); }}
                        autoFocus maxLength={80} placeholder="leave blank to clear override"
                        data-testid={`household-rename-input-${m.user_id}`}
                        className="flex-1 min-w-[140px] px-2 py-1 border hairline rounded text-xs bg-white" />
                      <button onClick={() => saveRename(m)} data-testid={`household-rename-save-${m.user_id}`}
                        className="text-[10px] px-2.5 py-1 rounded bg-[#4A5D4E] text-white">Save</button>
                      <button onClick={cancelRename} className="text-[10px] px-2 py-1 text-[#6B6A68]">Cancel</button>
                    </div>
                  );
                }
                if (isEditingMode) {
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 flex-wrap bg-[#F4F8F5] border border-[#B9D4BE] rounded px-2.5 py-2" data-testid={`household-member-edit-${m.user_id}`}>
                      <span className="text-sm text-[#1C1B1A] flex-1 min-w-[100px] truncate">{m.name}</span>
                      <select value={pendingMode} onChange={(e) => setPendingMode(e.target.value)}
                        data-testid={`household-mode-select-${m.user_id}`}
                        className="text-xs px-2 py-1.5 border hairline rounded bg-white focus:outline-none focus:border-[#4A5D4E]">
                        {COLLAB_MODE_OPTS.map((o) => (
                          <option key={o.v} value={o.v}>{o.label} — {o.hint}</option>
                        ))}
                      </select>
                      <button onClick={() => savePendingMode(m)} data-testid={`household-mode-save-${m.user_id}`}
                        className="text-[10px] px-2.5 py-1 rounded bg-[#4A5D4E] text-white">Save</button>
                      <button onClick={cancelEditMember} className="text-[10px] px-2 py-1 text-[#6B6A68]">Cancel</button>
                    </div>
                  );
                }
                return (
                  <div key={m.user_id}
                    className={`flex items-center gap-2 flex-wrap border hairline rounded px-2.5 py-2 ${isTheOwner ? "bg-[#FBF7EE] border-[#EAD9AE]" : "bg-white"} ${m.exempt ? "opacity-60" : ""}`}
                    data-testid={`household-member-${m.user_id}`}>
                    <span className={`text-sm text-[#1C1B1A] truncate min-w-[80px] shrink-0 flex items-center gap-1.5 ${m.exempt ? "grayscale" : ""}`}>
                      {isTheOwner && <span className="text-base">{m.emoji || "👑"}</span>}
                      {m.name}
                    </span>
                    <div className="flex-1 flex items-center justify-center gap-1.5">
                      <button onClick={() => setChoresTarget(m)} data-testid={`household-chores-btn-${m.user_id}`}
                        title="Which chores can this person do?"
                        className="text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border border-[#B5B3AE] bg-gradient-to-b from-[#F5F4F1] to-[#E4E1DA] text-[#3A3A38] hover:from-[#EBEAE6] hover:to-[#D7D4CC] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(0,0,0,0.08)] font-medium">
                        {tChoresBtn}
                      </button>
                      {isOwner && (
                        <button onClick={() => toggleExempt(m)}
                          data-testid={`member-exempt-toggle-${m.user_id}`}
                          title={m.exempt
                            ? "Hidden — click to re-enable and put back in the scheduling mix"
                            : "Active — click to hide from scheduling, leaderboard, and Smart Create (settings preserved)"}
                          className={`p-1.5 rounded-md border transition-colors ${
                            m.exempt
                              ? "bg-[#F4E4E4] border-[#D8B4B4] text-[#8A4A4A] hover:bg-[#EBD4D4]"
                              : "bg-white border-[#B5B3AE] text-[#3A3A38] hover:bg-[#F5F4F1]"
                          }`}>
                          {m.exempt ? <EyeSlash size={12} weight="duotone" /> : <Eye size={12} weight="duotone" />}
                        </button>
                      )}
                      <button onClick={() => setScheduleTarget(m)} data-testid={`household-schedule-btn-${m.user_id}`}
                        title="When is this person available?"
                        className="text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border border-[#B5B3AE] bg-gradient-to-b from-[#F5F4F1] to-[#E4E1DA] text-[#3A3A38] hover:from-[#EBEAE6] hover:to-[#D7D4CC] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(0,0,0,0.08)] font-medium">
                        {tScheduleBtn}
                      </button>
                    </div>
                    {isTheOwner ? (
                      // Feb 2026 pt89f (Randy) — Owner is ALWAYS a Parent.
                      // Not a toggle. Permanent lit tag.
                      <span data-testid={`household-parent-permanent-${m.user_id}`}
                        title="The silo owner is always a Parent — this can't be turned off."
                        className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 border bg-[#2C6E92] text-white border-[#2C6E92]">
                        Parent
                      </span>
                    ) : (
                      <button onClick={() => toggleParent(m)} title={m.is_parent
                          ? "Promoted parent — sees the Family digest. Tap to demote."
                          : "Promote this family member to Parent — gives them the Family digest, no billing change."}
                        data-testid={`household-parent-toggle-${m.user_id}`}
                        className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 border transition-colors ${
                          m.is_parent
                            ? "bg-[#2C6E92] text-white border-[#2C6E92] hover:bg-[#245878]"
                            : "bg-white text-[#6B6A68] border-black/10 hover:border-[#2C6E92] hover:text-[#2C6E92]"
                        }`}>
                        {m.is_parent ? "Parent" : "+ Parent"}
                      </button>
                    )}
                    <span className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 ${currentOpt.pillCls}`}
                      title={currentOpt.hint} data-testid={`household-mode-pill-${m.user_id}`}>
                      {currentOpt.short}
                    </span>
                    {!isTheOwner && (
                      <button onClick={() => startEditMember(m)} title="Change how their adds land"
                        data-testid={`household-mode-edit-${m.user_id}`}
                        className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                        <PencilSimple size={13} />
                      </button>
                    )}
                    <button onClick={() => startRename(m)}
                      title={isTheOwner ? "Rename yourself for calendar display only (doesn't touch your account)" : "Rename this person for calendar display only (doesn't touch their account)"}
                      data-testid={`household-rename-btn-${m.user_id}`}
                      className="p-1.5 rounded text-[#6B6A68] hover:text-[#8A6A24] hover:bg-[#F8F1E2]">
                      <PencilSimple size={13} weight="duotone" />
                    </button>
                    {/* Feb 2026 (Randy) — Impersonation eye (between rename
                        pencil and trash). Owner-only, and never on self. */}
                    {!isTheOwner && isOwner && (
                      <button onClick={() => impersonate(m)}
                        title="Step into this member's view (what they see when they log in)"
                        data-testid={`household-impersonate-btn-${m.user_id}`}
                        className="p-1.5 rounded text-[#6B6A68] hover:text-[#2C6E92] hover:bg-[#E3EFF7]">
                        <Eye size={13} weight="duotone" />
                      </button>
                    )}
                    {/* Feb 2026 (Randy) — PIN button. Only meaningful when
                        this row is Owner OR has the Parent pill lit. Others
                        show it disabled (obsolete) — per spec: field stays
                        in place either way, so the UI shows the icon greyed
                        out to communicate the state clearly. */}
                    {(isTheOwner || m.is_parent) && (
                      <button onClick={() => setPinRow({ user_id: m.user_id, pin: "" })}
                        title={pinMap[m.user_id] ? "PIN set — tap to change or clear" : "Set a 4-digit PIN so this parent can confirm on the Master Dashboard"}
                        data-testid={`household-pin-btn-${m.user_id}`}
                        className={`p-1.5 rounded ${pinMap[m.user_id] ? "text-[#2C6E92] hover:bg-[#E3EFF7]" : "text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"}`}>
                        <LockKey size={13} weight={pinMap[m.user_id] ? "fill" : "duotone"} />
                      </button>
                    )}
                    {!isTheOwner && (
                      <button onClick={() => removeCollab(m)}
                        title="Remove from this silo — mirrors Settings → Users"
                        data-testid={`household-delete-btn-${m.user_id}`}
                        className="p-1.5 rounded text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FBEBE4]">
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {canEditHousehold && (
        <KidProfilesPanel
          siloId={siloId}
          kids={kids}
          onChoresClick={(m) => setChoresTarget(m)}
          onScheduleClick={(m) => setScheduleTarget(m)}
          onReload={onChanged}
        />
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5 flex items-center gap-1.5">
          <Moon size={12} weight="duotone" /> {tQuietRow}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="time" value={qs} onChange={(e) => setQs(e.target.value)} data-testid="quiet-start-input"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white" />
          <span className="text-[10px] text-[#6B6A68]">to</span>
          <input type="time" value={qe} onChange={(e) => setQe(e.target.value)} data-testid="quiet-end-input"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white" />
          <button onClick={() => saveQuiet(false)} disabled={savingQuiet || !qs || !qe} data-testid="quiet-save-btn"
            className="text-[10px] px-2.5 py-1.5 rounded bg-[#1C1B1A] text-white disabled:opacity-50">Save</button>
          {(settings?.quiet_hours?.quiet_start || qs) && (
            <button onClick={() => saveQuiet(true)} disabled={savingQuiet} data-testid="quiet-clear-btn"
              className="text-[10px] underline text-[#6B6A68] hover:text-[#C16E5A]">clear</button>
          )}
        </div>
      </div>
      </>)}

      {/* Feb 2026 (Randy pt89b) — Smart Create Chores Schedule (renamed
          from Smart Create All Chores), with Master File + Wipe All
          Chores peer buttons below the plan textarea. */}
      {canEditHousehold && <SmartCreateAllChores siloId={siloId} siloName={siloName} siloKind={settings?.silo_kind} />}

      {choresTarget && (
        <KidChoresModal
          siloId={siloId}
          targetId={choresTarget.user_id}
          targetName={choresTarget.name}
          initialExcluded={choresTarget.excluded_chore_ids || []}
          onClose={() => setChoresTarget(null)}
          onSaved={onChanged}
        />
      )}
      {scheduleTarget && (
        <KidScheduleModal
          siloId={siloId}
          targetId={scheduleTarget.user_id}
          targetName={scheduleTarget.name}
          initialAvailability={scheduleTarget.availability}
          initialNotes={scheduleTarget.availability_notes}
          copyCandidates={copyCandidatesFor(scheduleTarget)}
          onClose={() => setScheduleTarget(null)}
          onSaved={onChanged}
        />
      )}
      {pinRow && (
        <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPinRow(null)}>
          <div className="modal-safe bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="household-pin-modal">
            <h3 className="font-serif-display text-lg flex items-center gap-2 mb-1">
              <LockKey size={16} weight="duotone" className="text-[#2C6E92]" /> Parent PIN
            </h3>
            <p className="text-xs text-[#6B6A68] mb-4 leading-relaxed">
              Set a 4-digit PIN so this parent can confirm a red-pending chore or reward on the Master Dashboard. Leave blank + Save to clear.
            </p>
            <input value={pinRow.pin} onChange={(e) => setPinRow({ ...pinRow, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="e.g. 2468" inputMode="numeric" autoFocus data-testid="household-pin-input"
              className="w-full px-3 py-2 border hairline rounded text-lg tracking-widest text-center bg-white focus:outline-none focus:border-[#2C6E92]" />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setPinRow(null)} className="text-xs px-3 py-2 text-[#6B6A68]">Cancel</button>
              <button onClick={() => savePin(pinRow.user_id, "")} data-testid="household-pin-clear"
                className="text-xs px-3 py-2 text-[#C16E5A] hover:underline">Clear PIN</button>
              <button onClick={() => savePin(pinRow.user_id, pinRow.pin)} disabled={pinRow.pin.length !== 4} data-testid="household-pin-save"
                className="text-xs px-4 py-2 rounded-md bg-[#2C6E92] text-white disabled:opacity-40">Save PIN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HouseholdPanel;
